import type { JournalArticle } from "./journal-types";

/** Cluster: luchthavenvervoer. */
export const LUCHTHAVEN_ARTICLES: JournalArticle[] = [
  {
    slug: "taxi-naar-het-vliegveld-of-zelf-rijden",
    title: "Taxi naar het vliegveld of zelf rijden: wat is slimmer?",
    seoTitle: "Taxi naar het vliegveld of zelf rijden? | Taxiritje.nl",
    description:
      "Met de taxi naar de luchthaven of zelf rijden? Vergelijk kosten, parkeren, reistijd en gemak, en ontdek wat bij uw reis past.",
    category: "luchthaven",
    intent: "Vergelijkend: reiziger kiest tussen eigen auto en taxi naar de luchthaven.",
    primaryKeyword: "taxi naar het vliegveld of zelf rijden",
    published: "2026-07-14",
    readingMinutes: 6,
    intro:
      "Zelf naar de luchthaven rijden geeft controle, maar zorgt ook voor parkeerkosten, zoektijd en een auto die dagenlang stilstaat. Een taxi levert dat allemaal in ruil voor één vaste ritprijs. Welke keuze verstandiger is, hangt vooral af van uw reisduur en het aantal reizigers.",
    sections: [
      {
        heading: "Reken uw reisduur mee, niet alleen de ritprijs",
        paragraphs: [
          "Parkeren bij een luchthaven wordt per dag of dagdeel afgerekend. Bij een weekendtrip valt dat mee, maar bij een reis van tien dagen tellen die kosten flink door. Vergelijk daarom altijd het parkeertarief voor uw volledige reisduur met de ritprijs heen én terug.",
          "Actuele parkeertarieven en beschikbaarheid vindt u op de officiële website van de luchthaven; die kunnen per periode en per terrein verschillen.",
        ],
      },
      {
        heading: "Wat u met een taxi niet hoeft te regelen",
        paragraphs: [
          "Naast geld speelt gemak een grote rol. Een deel van het luchthavenproces verdwijnt eenvoudigweg wanneer u zich laat brengen.",
        ],
        bullets: [
          "Geen parkeerplek zoeken op een druk terrein",
          "Geen shuttlebus met koffers naar de terminal",
          "Geen zorgen over uw auto tijdens de reis",
          "Geen vermoeide rit naar huis na een nachtvlucht",
        ],
      },
      {
        heading: "Zelf rijden versus taxi",
        paragraphs: [
          "In grote lijnen ziet de afweging er zo uit. Vul de bedragen in voor uw eigen reis, dan wordt de keuze meestal snel duidelijk.",
        ],
        table: {
          columns: ["Aspect", "Zelf rijden", "Taxi"],
          rows: [
            ["Kosten", "Brandstof + parkeren per dag", "Eenmalige ritprijs heen en terug"],
            ["Tijd bij de luchthaven", "Parkeren + shuttle", "Uitstappen bij de vertrekhal"],
            ["Bagage", "Zelf van parkeerterrein naar terminal", "Tot aan de terminal"],
            ["Terugreis na een nachtvlucht", "Zelf rijden, vermoeid", "U wordt opgehaald"],
            ["Flexibiliteit ter plaatse", "Auto blijft beschikbaar", "Nieuwe rit nodig"],
          ],
        },
      },
      {
        heading: "Wanneer zelf rijden toch beter uitpakt",
        paragraphs: [
          "Reist u alleen, voor één nacht, en woont u dicht bij de luchthaven met een goedkoop parkeertarief? Dan blijft de eigen auto vaak de voordeligste optie. Ook als u na landing direct dóór moet naar een adres in de buurt van de luchthaven, kan een eigen auto praktischer zijn.",
        ],
      },
      {
        heading: "Praktische zaken bij een luchthavenrit",
        paragraphs: [
          "Kiest u voor een taxi, geef dan uw vluchtnummer door. Bij de terugreis wordt gewerkt met de landingstijd in plaats van een ophaaltijd, zodat de planning aansluit op het moment waarop u daadwerkelijk aankomt. Geef ook het aantal grote koffers door: bagage bepaalt mede welk voertuigtype nodig is.",
        ],
      },
    ],
    conclusion:
      "Hoe langer uw reis en hoe meer reizigers, hoe eerder de taxi wint. Bij een korte solotrip kan zelf rijden voordeliger zijn. Reken één keer eerlijk door — parkeertarief maal het aantal dagen tegenover de ritprijs heen en terug — en u weet het meteen.",
    faq: [
      {
        q: "Is een taxi naar de luchthaven duurder dan parkeren?",
        a: "Dat hangt af van uw reisduur. Parkeerkosten lopen per dag op, terwijl een taxirit een eenmalig bedrag is. Bij langere reizen of meerdere reizigers valt de vergelijking vaak in het voordeel van de taxi uit.",
      },
      {
        q: "Waarom is een vluchtnummer nodig?",
        a: "Met het vluchtnummer kan de planning worden afgestemd op uw daadwerkelijke vlucht, wat vooral bij de terugreis belangrijk is.",
      },
    ],
    links: [
      { label: "Luchthavenvervoer met vaste prijs", to: "/luchthaventaxi" },
      { label: "Parkeren bij het vliegveld of met de taxi?", to: "/journal/parkeren-bij-het-vliegveld-of-taxi" },
      { label: "Boek uw luchthavenrit", to: "/boeken" },
    ],
    sources: [{ label: "Parkeerinformatie Schiphol (officieel)", url: "https://www.schiphol.nl/nl/parkeren/" }],
  },
  {
    slug: "parkeren-bij-het-vliegveld-of-taxi",
    title: "Parkeren bij het vliegveld of taxi: praktische keuzehulp",
    seoTitle: "Parkeren bij het vliegveld of taxi: praktische keuzehulp",
    description:
      "Parkeren bij het vliegveld of taxi? Controleer loopafstand, shuttle, sleutelafgifte en terugreis. Praktische keuzehulp voor uw luchthavenvervoer.",
    category: "luchthaven",
    intent: "Praktisch: parkeerproduct, transfer naar terminal en terugreis controleren, zonder Schiphol-prijsvergelijking te dupliceren.",
    primaryKeyword: "parkeren vliegveld of taxi",
    published: "2026-07-21",
    readingMinutes: 5,
    intro:
      "Kies niet alleen op prijs: controleer hoe u van het parkeerterrein bij de terminal komt en hoe u na landing terugreist. Een taxi brengt u van deur tot terminal; bij parkeren bepalen loopafstand, shuttle, openingstijden en sleutelafgifte het gemak. Hieronder leest u wat u vooraf controleert.",
    sections: [
      {
        heading: "Controleer welk parkeerproduct u boekt",
        paragraphs: [
          "Parkeren op loopafstand, parkeren met een shuttle en valetparking zijn verschillende producten. Bekijk het exacte adres, de route naar de terminal en of u uw autosleutel moet afgeven. Lees ook welke partij het terrein beheert en wat er gebeurt als uw terugvlucht later aankomt.",
          "Controleer de actuele voorwaarden bij de luchthaven of aanbieder. Openingstijden, shuttles en wijzigingsmogelijkheden verschillen per product; ga niet af op alleen de naam van het parkeerterrein.",
        ],
      },
      {
        heading: "Terminaltransfer: plan het hele traject",
        paragraphs: [
          "Uw rit eindigt bij parkeren niet bij de terminal. Plan tijd voor inrijden, een plek vinden, bagage uitladen en de wandeling of shuttle. Controleer dit ook voor uw terugreis, vooral bij een late landing.",
        ],
        bullets: [
          "Hoe vaak rijdt de shuttle op uw vertrek- en aankomsttijd?",
          "Is de route met koffers, kinderen of een hulpmiddel goed te doen?",
          "Waar vindt u na landing de shuttle of uw auto?",
          "Wie belt u bij vertraging of een gewijzigd aankomstmoment?",
        ],
      },
      {
        heading: "Praktische verschillen naast elkaar",
        paragraphs: [
          "De juiste keuze hangt ook af van wat u na de vlucht nog wilt of kunt doen. Met een taxi hoeft niemand te rijden na een nachtvlucht. Bij parkeren heeft u uw eigen auto beschikbaar, maar moet u eerst terug naar het terrein.",
        ],
        table: {
          caption: "Praktische keuzehulp voor luchthavenvervoer",
          columns: ["Controlepunt", "Parkeren", "Vooraf geboekte taxi"],
          rows: [
            ["Terminal bereiken", "Lopen of shuttle, afhankelijk van product", "Uitstappen bij de terminal"],
            ["Na landing", "Terug naar terrein en zelf rijden", "Ophalen volgens vluchtgegevens"],
            ["Autosleutel", "Controleer sleutelafgifte bij aanbieder", "Eigen auto blijft thuis"],
            ["Vluchtdatum wijzigt", "Controleer parkeervoorwaarden", "Neem contact op voor de ritwijziging"],
          ],
        },
      },
      {
        heading: "Meer dan geld alleen",
        paragraphs: [
          "Ook als de bedragen dicht bij elkaar liggen, verschilt de ervaring. Parkeren betekent koffers naar een shuttle brengen, wachten en na terugkomst nog zelf rijden. Een taxi zet u af bij de vertrekhal en haalt u op zodra u binnen bent.",
        ],
      },
    ],
    conclusion:
      "Controleer het parkeerproduct, de terminaltransfer en de terugreis voordat u kiest. Voor de kostenvergelijking op Schiphol vindt u een apart artikel; deze keuzehulp draait om de praktische reis van deur tot terminal en terug.",
    faq: [
      {
        q: "Moet ik mijn autosleutel afgeven bij parkeren?",
        a: "Dat hangt af van het parkeerproduct. Controleer dit bij de aanbieder voordat u boekt; valetparking en zelf parkeren hebben verschillende voorwaarden.",
      },
      {
        q: "Hoe plan ik de transfer van parkeerterrein naar terminal?",
        a: "Controleer loopafstand of shuttlefrequentie op uw daadwerkelijke reistijd. Tel parkeren, uitladen en de transfer op bij uw rijtijd en de benodigde aanwezigheid op de luchthaven.",
      },
    ],
    links: [
      { label: "Luchthaventaxi met vaste prijs", to: "/luchthaventaxi" },
      { label: "Taxi naar het vliegveld of zelf rijden?", to: "/journal/taxi-naar-het-vliegveld-of-zelf-rijden" },
      { label: "Prijzen per route bekijken", to: "/prijzen" },
      { label: "Taxi of parkeren op Schiphol: kosten vergelijken", to: "/journal/goedkoper-taxi-of-parkeren-schiphol" },
    ],
    sources: [{ label: "Parkeertarieven Schiphol (officieel)", url: "https://www.schiphol.nl/nl/parkeren/" }],
  },
  {
    slug: "taxi-naar-het-vliegveld-bij-een-vroege-vlucht",
    title: "Taxi naar het vliegveld bij een vroege vlucht",
    seoTitle: "Taxi naar het vliegveld bij een vroege vlucht | Taxiritje.nl",
    description:
      "Vroege vlucht? Zo bepaalt u uw ophaaltijd, houdt u marge aan en voorkomt u stress als het openbaar vervoer nog niet rijdt.",
    category: "luchthaven",
    intent: "Praktisch: plannen van vervoer bij een vlucht in de vroege ochtend.",
    primaryKeyword: "taxi vroege vlucht",
    published: "2026-07-28",
    readingMinutes: 5,
    intro:
      "Bij een vlucht rond zes uur 's ochtends rijdt het openbaar vervoer vaak nog niet, of net niet vaak genoeg. Een vooraf geboekte taxi lost dat op — mits u de ophaaltijd goed terugrekent.",
    sections: [
      {
        heading: "Reken terug vanaf uw vertrektijd",
        paragraphs: [
          "Begin bij de vertrektijd van uw vlucht en werk terug: aanwezigheidstijd op de luchthaven, reistijd naar de luchthaven en een marge voor onverwachte drukte. Voor Europese vluchten wordt doorgaans ongeveer twee uur aanwezigheid geadviseerd, voor intercontinentale vluchten drie uur. Uw luchtvaartmaatschappij geeft hierover de leidende informatie.",
        ],
        bullets: [
          "Vertrektijd vlucht",
          "min aanwezigheidstijd (2 uur Europees, 3 uur intercontinentaal)",
          "min reistijd naar de luchthaven",
          "min marge van 15 tot 30 minuten",
          "= uw ophaaltijd",
        ],
      },
      {
        heading: "Waarom de nacht rustiger rijdt — maar niet altijd",
        paragraphs: [
          "In de vroege ochtend is het op de weg meestal rustig, waardoor de reistijd korter is dan overdag. Maar rond vier tot zes uur 's ochtends is het bij grote luchthavens juist druk in de vertrekhal, omdat veel vluchten tegelijk vertrekken. Houd daar rekening mee bij het bepalen van uw aanwezigheidstijd.",
        ],
      },
      {
        heading: "Bereid de avond ervoor voor",
        paragraphs: [
          "Hoe minder u 's ochtends hoeft te bedenken, hoe soepeler het gaat.",
        ],
        bullets: [
          "Zet koffers de avond ervoor bij de deur",
          "Controleer uw ophaaladres inclusief huisnummer en postcode",
          "Leg reisdocumenten en boardingpass klaar",
          "Zet twee wekkers en houd uw telefoon opgeladen",
        ],
      },
      {
        heading: "Boek op tijd",
        paragraphs: [
          "Vroege ochtendritten zijn gewild: veel reizigers willen tussen vier en zes uur worden opgehaald. Reserveer daarom ruim van tevoren, zeker in vakantieperiodes en rond feestdagen.",
        ],
      },
    ],
    conclusion:
      "Reken uw ophaaltijd terug vanaf de vertrektijd, tel een marge op en boek de rit ruim vooraf. Zo begint een vroege vlucht met een rustige rit in plaats van met een sprint naar de eerste trein.",
    faq: [
      {
        q: "Hoe laat moet ik vertrekken bij een vlucht om 06:00 uur?",
        a: "Bij een Europese vlucht is twee uur aanwezigheid gebruikelijk, dus rond 04:00 uur op de luchthaven. Tel daar uw reistijd en een marge bij op om de ophaaltijd te bepalen.",
      },
      {
        q: "Rijdt er 's nachts openbaar vervoer naar de luchthaven?",
        a: "Op sommige trajecten wel, maar met een lage frequentie. Controleer dit vooraf in de reisplanner; een gereserveerde taxi is in die uren doorgaans betrouwbaarder.",
      },
    ],
    links: [
      { label: "Luchthaventaxi boeken", to: "/luchthaventaxi" },
      { label: "Hoe laat moet u vertrekken naar de luchthaven?", to: "/journal/luchthaventaxi-hoe-laat-vertrekken" },
      { label: "Taxi in de nacht", to: "/journal/taxi-in-de-nacht" },
    ],
  },
  {
    slug: "met-veel-bagage-naar-het-vliegveld",
    title: "Met veel bagage naar het vliegveld: taxi of openbaar vervoer?",
    seoTitle: "Met veel bagage naar het vliegveld: taxi of ov? | Taxiritje.nl",
    description:
      "Veel koffers mee naar de luchthaven? Vergelijk taxi en openbaar vervoer op gemak, tijd en risico, en kies het juiste voertuigtype.",
    category: "luchthaven",
    intent: "Praktisch/vergelijkend: reizen met veel bagage naar de luchthaven.",
    primaryKeyword: "met veel bagage naar het vliegveld",
    published: "2026-08-04",
    readingMinutes: 4,
    intro:
      "Bagage is de stille bepalende factor bij de keuze van vervoer naar de luchthaven. Twee koffers zijn nog te doen; vier koffers met een kinderwagen erbij maken elke overstap tot een operatie.",
    sections: [
      {
        heading: "Wat bagage doet met een ov-reis",
        paragraphs: [
          "In het openbaar vervoer draagt u alles zelf: van huis naar de halte, de trap op, door de drukte en soms nog een stuk vanaf het station. Bij een overstap moet dat allemaal binnen de overstaptijd. Bij drukte is er bovendien niet altijd ruimte om koffers neer te zetten.",
        ],
      },
      {
        heading: "Kies het juiste voertuigtype",
        paragraphs: [
          "Ook in een taxi is ruimte niet oneindig. Een personenauto biedt plaats aan een beperkt aantal grote koffers; bij meer bagage of meer reizigers is een groter voertuig nodig. Geef bij het reserveren daarom apart het aantal grote koffers en kleine bagagestukken op, zodat het juiste voertuig wordt ingezet.",
        ],
        bullets: [
          "Tel grote koffers en handbagage apart",
          "Vermeld bijzondere bagage zoals ski's, golftassen of een kinderwagen",
          "Bij twijfel: kies liever een ruimer voertuig",
        ],
      },
      {
        heading: "De vergelijking op een rij",
        paragraphs: ["Bij veel bagage verschuift de afweging duidelijk."],
        table: {
          columns: ["Aspect", "Taxi", "Openbaar vervoer"],
          rows: [
            ["Dragen", "Alleen in- en uitladen", "Volledige reis zelf dragen"],
            ["Overstappen", "Geen", "Met bagage door drukte"],
            ["Ruimte", "Voertuigtype op maat", "Beperkt en niet gegarandeerd"],
            ["Bij vertraging", "Rit blijft één geheel", "Nieuwe aansluiting zoeken"],
          ],
        },
      },
    ],
    conclusion:
      "Hoe meer u meeneemt, hoe zwaarder het gemak weegt. Reist u met meerdere koffers, dan is een taxi met het juiste voertuigtype vrijwel altijd de rustigste keuze — geef alleen wel vooraf door hoeveel bagage er echt meegaat.",
    faq: [
      {
        q: "Hoeveel koffers passen er in een taxi?",
        a: "Dat verschilt per voertuigtype. Een personenauto is geschikt voor een beperkt aantal grote koffers; bij meer bagage wordt een groter voertuig ingezet. Geef daarom bij het boeken het exacte aantal bagagestukken op.",
      },
      {
        q: "Kost extra bagage meer?",
        a: "Bagage kan bepalen welk voertuigtype nodig is, en een groter voertuig heeft een ander tarief. U ziet dat verschil voordat u de boeking bevestigt.",
      },
    ],
    links: [
      { label: "Luchthaventaxi boeken", to: "/luchthaventaxi" },
      { label: "Taxibus met meerdere personen", to: "/journal/taxibus-met-meerdere-personen" },
      { label: "Taxi boeken", to: "/boeken" },
    ],
  },
  {
    slug: "taxi-naar-het-vliegveld-met-kinderen",
    title: "Taxi naar het vliegveld met kinderen",
    seoTitle: "Taxi naar het vliegveld met kinderen: zo plant u het | Taxiritje.nl",
    description:
      "Met kinderen naar de luchthaven? Praktische tips over timing, bagage, kinderzitjes, kinderwagens en rust onderweg.",
    category: "luchthaven",
    intent: "Praktisch: gezinnen plannen hun rit naar de luchthaven.",
    primaryKeyword: "taxi vliegveld met kinderen",
    published: "2026-08-11",
    readingMinutes: 4,
    intro:
      "Met kinderen reizen betekent meer bagage, meer momenten van oponthoud en minder tolerantie voor wachten. Een rechtstreekse rit van deur tot deur haalt precies die onzekerheden weg.",
    sections: [
      {
        heading: "Plan iets ruimer dan u zou doen",
        paragraphs: [
          "Reken bij een reis met jonge kinderen op extra tijd voor instappen, toiletbezoek en het laden van bagage. Vijftien tot dertig minuten extra marge voorkomt dat u de dag begint met haast.",
        ],
      },
      {
        heading: "Kinderzitjes en veiligheid",
        paragraphs: [
          "In Nederland gelden wettelijke regels voor het vervoeren van kinderen in auto's, waarbij voor taxi's afwijkende bepalingen bestaan. De actuele regels leest u bij de Rijksoverheid. Wilt u zeker weten dat uw kind in een vertrouwd zitje reist, dan kunt u uw eigen kinderzitje meenemen; geef dat wel bij het boeken door, want het neemt ruimte in.",
        ],
      },
      {
        heading: "Kinderwagen en bagage",
        paragraphs: [
          "Een kinderwagen telt als bagage en bepaalt mee welk voertuig nodig is. Geef bij het reserveren aan dat u een wagen meeneemt, samen met het aantal grote koffers, zodat alles daadwerkelijk meekan.",
        ],
        bullets: [
          "Kinderwagen: opvouwbaar of niet?",
          "Aantal grote koffers en handbagagestukken",
          "Extra tas met eten, drinken en spelletjes binnen handbereik",
        ],
      },
      {
        heading: "Rust onderweg",
        paragraphs: [
          "Een directe rit zonder overstappen scheelt kinderen veel prikkels. Zeker bij vroege vluchten kunnen kinderen in de auto vaak nog even doorslapen — iets wat op een perron niet lukt.",
        ],
      },
    ],
    conclusion:
      "Reis met kinderen zo direct mogelijk, plan extra marge en geef kinderwagen en bagage vooraf door. Dan begint de vakantie al bij de voordeur in plaats van pas bij de gate.",
    faq: [
      {
        q: "Mag mijn kind zonder kinderzitje in een taxi?",
        a: "Voor taxi's gelden andere regels dan voor gewone personenauto's. Raadpleeg de actuele voorschriften bij de Rijksoverheid en geef bij twijfel door dat u een eigen zitje meeneemt.",
      },
      {
        q: "Kan ik een eigen kinderzitje meenemen?",
        a: "Ja. Geef dit bij het boeken aan, zodat er rekening wordt gehouden met de ruimte die het inneemt.",
      },
    ],
    links: [
      { label: "Met kinderen in de taxi", to: "/journal/met-kinderen-in-de-taxi" },
      { label: "Luchthaventaxi boeken", to: "/luchthaventaxi" },
      { label: "Veelgestelde vragen", to: "/faq" },
    ],
    sources: [
      {
        label: "Regels voor kinderen in de auto en taxi (Rijksoverheid)",
        url: "https://www.rijksoverheid.nl/themas/verkeer-en-vervoer/verkeersveiligheid",
      },
    ],
  },
];
